// ✅ src/components/admin/AdminNotificationsScreen.js (FULL)
// ✅ WORKS WITH your user NotificationsScreen + notify.js
// ✅ Writes to: notifications/{uid}/items
// ✅ Search user by name/email/phone (optional)
// ✅ Manual UID fallback (best / always works)
// ✅ Broadcast to all users (reads /users, needs rules/index)
// ✅ NEW: Optional "Send email too" -> triggers Cloud Function emailOnNotification
// ✅ NEW: LIVE EMAIL DEBUG on this screen (listens to the last sent notif doc)
// ❌ Debug UI removed (as requested)  -> (this is NOT the old debug UI; this is email status/debug only)

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { auth, db } from "../../lib/firebase";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";

/* ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}
function lower(v) {
  return safe(v).toLowerCase();
}
function isLikelyEmail(s) {
  const t = lower(s);
  return t.includes("@") && t.includes(".");
}
function isLikelyPhone(s) {
  const t = safe(s);
  const digits = t.replace(/[^\d+]/g, "");
  return digits.replace(/\D/g, "").length >= 6;
}

/** ✅ MUST MATCH USER APP LISTEN PATH: notifications/{uid}/items */
const NOTIFS_ROOT = "notifications";

export default function AdminNotificationsScreen({ goBack, role }) {
  const roleNorm = safe(role).toUpperCase();
  const isAllowed = roleNorm === "ADMIN" || roleNorm === "WORKER";
  const roleLoaded = !!safe(role);

  // USER | ALL
  const [mode, setMode] = useState("USER");

  // search
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [selectedUid, setSelectedUid] = useState("");

  // manual uid fallback
  const [manualUid, setManualUid] = useState("");

  // compose
  const [type, setType] = useState("ADMIN_MESSAGE");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");

  // ✅ email toggle
  const [sendEmailToo, setSendEmailToo] = useState(true);

  // state
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // ✅ LIVE DEBUG (last sent notification doc) — ALWAYS VISIBLE
  const [lastDocPath, setLastDocPath] = useState("");
  const [emailDebug, setEmailDebug] = useState({
    emailStatus: "IDLE",
    emailDebug: "Send a notification to see live email status here.",
    emailSent: null,
    emailError: "",
  });
  const unsubRef = useRef(null);

  // which uid to send to
  const toUid = useMemo(() => safe(selectedUid) || safe(manualUid), [selectedUid, manualUid]);

  const pid = auth.app?.options?.projectId || "unknown-project";
  const adminUid = auth.currentUser?.uid || "";

  useEffect(() => {
    // optional breadcrumbs
    console.log("🔎 [ADMIN NOTIFS] project:", pid);
    console.log("🔎 [ADMIN NOTIFS] root:", NOTIFS_ROOT);
    console.log("🔎 [ADMIN NOTIFS] adminUid:", adminUid);
  }, [pid, adminUid]);

  const canSend = useMemo(() => {
    if (!roleLoaded) return false;
    if (!isAllowed) return false;
    if (!safe(title) || !safe(message)) return false;
    if (mode === "USER" && !safe(toUid)) return false;
    return true;
  }, [roleLoaded, isAllowed, title, message, mode, toUid]);

  // debounce timer
  const tRef = useRef(null);

  const runSearch = useCallback(async () => {
    const qText = safe(userQuery);
    const qLower = lower(qText);

    if (!roleLoaded || !isAllowed) {
      setUserResults([]);
      return;
    }
    if (mode !== "USER") {
      setUserResults([]);
      return;
    }
    if (!qText || qText.length < 2) {
      setUserResults([]);
      return;
    }

    try {
      setErr("");

      const usersRef = collection(db, "users");
      const queriesToRun = [];

      // Best: displayNameLower prefix search
      queriesToRun.push(
        query(
          usersRef,
          where("displayNameLower", ">=", qLower),
          where("displayNameLower", "<=", qLower + "\uf8ff"),
          limit(10)
        )
      );

      // Best: emailLower prefix search
      queriesToRun.push(
        query(usersRef, where("emailLower", ">=", qLower), where("emailLower", "<=", qLower + "\uf8ff"), limit(10))
      );

      // Fallback: displayName case-sensitive
      queriesToRun.push(
        query(usersRef, where("displayName", ">=", qText), where("displayName", "<=", qText + "\uf8ff"), limit(10))
      );

      // Fallback: email case-sensitive if looks like email
      if (isLikelyEmail(qText)) {
        queriesToRun.push(
          query(usersRef, where("email", ">=", qText), where("email", "<=", qText + "\uf8ff"), limit(10))
        );
      }

      // Phone if looks like phone
      if (isLikelyPhone(qText)) {
        queriesToRun.push(
          query(usersRef, where("phone", ">=", qText), where("phone", "<=", qText + "\uf8ff"), limit(10))
        );
      }

      const results = await Promise.all(
        queriesToRun.map(async (qq) => {
          try {
            const snap = await getDocs(qq);
            return { snap, error: null };
          } catch (error) {
            return { snap: null, error };
          }
        })
      );

      // permission denied?
      const permErr = results.find((r) => {
        const m = String(r?.error?.message || "").toLowerCase();
        return m.includes("permission") || m.includes("missing or insufficient permissions");
      });

      if (permErr) {
        const m = permErr.error?.message || "permission-denied";
        setUserResults([]);
        setErr(m + " — rules block reading /users. Use Manual UID or allow ADMIN/WORKER read access.");
        return;
      }

      // merge & dedupe
      const all = [];
      for (const r of results) {
        if (r.snap) r.snap.docs.forEach((d) => all.push({ id: d.id, ...d.data() }));
      }
      const map = new Map();
      for (const u of all) map.set(u.id, u);

      setUserResults(Array.from(map.values()).slice(0, 12));

      // index warning?
      const nonPerm = results.find((r) => r?.error);
      if (nonPerm) {
        const mm = String(nonPerm.error?.message || "").toLowerCase();
        if (mm.includes("index")) setErr("Missing index: Firestore needs an index. Open console error link and create it.");
      }
    } catch (e) {
      setErr(e?.message || "Search failed");
      setUserResults([]);
    }
  }, [userQuery, roleLoaded, isAllowed, mode]);

  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    if (mode !== "USER") return;

    tRef.current = setTimeout(() => {
      runSearch();
    }, 250);

    return () => {
      if (tRef.current) clearTimeout(tRef.current);
    };
  }, [mode, runSearch]);

  // ✅ clean up listener on unmount
  useEffect(() => {
    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, []);

  function listenToNotifDoc(uid, notifId) {
    try {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }

      setEmailDebug({
        emailStatus: "LISTENING",
        emailDebug: "Waiting for function update...",
        emailSent: null,
        emailError: "",
      });

      const dref = doc(db, NOTIFS_ROOT, uid, "items", notifId);
      const path = `${NOTIFS_ROOT}/${uid}/items/${notifId}`;
      setLastDocPath(path);

      const unsub = onSnapshot(
        dref,
        (s) => {
          const d = s.data() || {};
          setEmailDebug({
            emailStatus: d.emailStatus || (d.emailSent ? "SENT" : "—"),
            emailDebug: d.emailDebug || (d.emailSent ? "Sent ✅" : "Waiting..."),
            emailSent: d.emailSent ?? null,
            emailError: d.emailError || "",
          });
        },
        (e) => {
          setEmailDebug({
            emailStatus: "ERROR",
            emailDebug: "Snapshot listener failed (check rules)",
            emailSent: false,
            emailError: e?.message || String(e),
          });
        }
      );

      unsubRef.current = unsub;

      // auto-stop after 2 minutes
      setTimeout(() => {
        if (unsubRef.current === unsub) {
          unsub();
          unsubRef.current = null;
          setEmailDebug((prev) => ({
            ...prev,
            emailStatus: prev?.emailStatus || "STOPPED",
            emailDebug: prev?.emailDebug || "Listener stopped after 2 minutes.",
          }));
        }
      }, 120_000);
    } catch (e) {
      setEmailDebug({
        emailStatus: "ERROR",
        emailDebug: "Failed to start listener",
        emailSent: false,
        emailError: e?.message || String(e),
      });
    }
  }

  /** ✅ send to one user */
  async function sendToUid(sendUid, payload) {
    const admin = auth.currentUser?.uid || "admin";

    const subj = safe(payload?.title) || "Notification";
    const body = safe(payload?.message) || "";

    const ref = await addDoc(collection(db, NOTIFS_ROOT, sendUid, "items"), {
      type: safe(payload?.type) || "ADMIN_MESSAGE",
      title: subj,
      message: body,
      data: payload?.data && typeof payload.data === "object" ? payload.data : { fromAdmin: true },

      // ✅ triggers email function (optional)
      sendEmail: !!payload?.sendEmail,
      emailSubject: subj,
      emailBody: body,

      // ✅ debug placeholders (so you see something immediately)
      emailStatus: !!payload?.sendEmail ? "QUEUED" : "SKIPPED",
      emailDebug: !!payload?.sendEmail ? "Queued for email sending..." : "Email disabled for this notif",

      read: false,
      createdAt: serverTimestamp(),
      createdAtMs: Date.now(),
      createdBy: admin,
    });

    return ref;
  }

  /** Broadcast (needs /users read permission) */
  async function broadcastToUsers(uids, payload) {
    const admin = auth.currentUser?.uid || "admin";
    const CHUNK = 400;

    const subj = safe(payload?.title) || "Notification";
    const body = safe(payload?.message) || "";

    for (let i = 0; i < uids.length; i += CHUNK) {
      const part = uids.slice(i, i + CHUNK);
      const batch = writeBatch(db);

      for (const uid of part) {
        const ref = doc(collection(db, NOTIFS_ROOT, uid, "items"));
        batch.set(ref, {
          type: safe(payload?.type) || "ADMIN_MESSAGE",
          title: subj,
          message: body,
          data: payload?.data && typeof payload.data === "object" ? payload.data : { fromAdmin: true },

          // ✅ triggers email function (optional)
          sendEmail: !!payload?.sendEmail,
          emailSubject: subj,
          emailBody: body,

          emailStatus: !!payload?.sendEmail ? "QUEUED" : "SKIPPED",
          emailDebug: !!payload?.sendEmail ? "Queued for email sending..." : "Email disabled for this notif",

          read: false,
          createdAt: serverTimestamp(),
          createdAtMs: Date.now(),
          createdBy: admin,
        });
      }

      await batch.commit();
    }
  }

  async function sendNow() {
    if (!canSend) return;

    setSending(true);
    setMsg("");
    setErr("");
    setLastDocPath("");

    // ✅ DO NOT SET NULL — keep it visible
    setEmailDebug({
      emailStatus: "SENDING",
      emailDebug: "Sending notification...",
      emailSent: null,
      emailError: "",
    });

    try {
      const payload = {
        type,
        title,
        message,
        data: { fromAdmin: true },
        sendEmail: sendEmailToo,
      };

      if (mode === "USER") {
        const target = safe(toUid);
        const ref = await sendToUid(target, payload);
        setMsg(`✅ Sent to UID: ${target} (doc: ${ref.id})`);

        // ✅ LIVE DEBUG LISTENER (single user only)
        listenToNotifDoc(target, ref.id);
      } else {
        const usersSnap = await getDocs(query(collection(db, "users"), limit(500)));
        const uids = usersSnap.docs.map((d) => d.id).filter(Boolean);

        if (!uids.length) {
          setErr("No users found to broadcast.");
          setEmailDebug({
            emailStatus: "ERROR",
            emailDebug: "No users found to broadcast.",
            emailSent: null,
            emailError: "",
          });
        } else {
          await broadcastToUsers(uids, payload);
          setMsg(`✅ Broadcast sent to ${uids.length} users!`);
          setEmailDebug({
            emailStatus: "BROADCAST_SENT",
            emailDebug: sendEmailToo
              ? "Broadcast queued. Email debug can’t show all users here (check Functions logs)."
              : "Broadcast sent (email off).",
            emailSent: null,
            emailError: "",
          });
        }
      }

      // reset form
      setTitle("");
      setMessage("");
      setType("ADMIN_MESSAGE");
      setSelectedUid("");
      setManualUid("");
      setUserResults([]);
      setUserQuery("");
    } catch (e) {
      console.error(e);
      setErr(e?.message || "❌ Failed to send. Check rules / permissions.");
      setEmailDebug({
        emailStatus: "ERROR",
        emailDebug: "Failed to send notification.",
        emailSent: false,
        emailError: e?.message || String(e),
      });
    } finally {
      setSending(false);
    }
  }

  /** One-click test: fills inputs to send to admin itself */
  function testSendToMe() {
    const myUid = auth.currentUser?.uid;
    if (!myUid) {
      setErr("Admin not logged in.");
      setEmailDebug({
        emailStatus: "ERROR",
        emailDebug: "Admin not logged in.",
        emailSent: false,
        emailError: "",
      });
      return;
    }
    setManualUid(myUid);
    setSelectedUid("");
    setMode("USER");
    setType("SYSTEM_TEST");
    setTitle("Test Notification");
    setMessage("If you can see this in Notifications, your reading side is working ✅");
    setSendEmailToo(true);
    setMsg("✅ Filled. Now press Send.");

    setEmailDebug({
      emailStatus: "READY",
      emailDebug: "Filled test notification. Press Send to run email debug listener.",
      emailSent: null,
      emailError: "",
    });
  }

  /* ---------------- UI guards ---------------- */
  if (!roleLoaded) {
    return (
      <div style={S.page}>
        <div style={S.top}>
          <button style={S.back} onClick={goBack} type="button">
            ←
          </button>
          <div>
            <div style={S.h1}>Admin Notifications</div>
            <div style={S.sub}>Loading role...</div>
          </div>
        </div>
        <div style={S.panel}>Loading...</div>
      </div>
    );
  }

  if (!isAllowed) {
    return (
      <div style={S.page}>
        <div style={S.top}>
          <button style={S.back} onClick={goBack} type="button">
            ←
          </button>
          <div>
            <div style={S.h1}>Admin Notifications</div>
            <div style={S.sub}>Access denied</div>
          </div>
        </div>
        <div style={S.panel}>
          You need <b>ADMIN</b> or <b>WORKER</b>.
          <div style={{ marginTop: 8, fontWeight: 900, fontSize: 12, color: "rgba(15,23,42,0.65)" }}>
            Your role: <b>{roleNorm || "—"}</b>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------- UI ---------------- */
  return (
    <div style={S.page}>
      <div style={S.top}>
        <button style={S.back} onClick={goBack} type="button">
          ←
        </button>

        <div style={{ minWidth: 0 }}>
          <div style={S.h1}>Admin Notifications</div>
          <div style={S.sub}>Send to one user or broadcast.</div>

          <div style={{ marginTop: 6, fontSize: 11, opacity: 0.75, fontWeight: 900 }}>
            root: <b>{NOTIFS_ROOT}</b> • project: <b>{pid}</b> • adminUid: <b>{adminUid || "—"}</b>
          </div>
        </div>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button style={S.smallBtn} onClick={testSendToMe} type="button">
            Test fill (to me)
          </button>
          <span style={S.badge}>{roleNorm}</span>
        </div>
      </div>

      <div style={S.wrap}>
        <div style={S.panel}>
          <div style={S.row}>
            <button
              style={{ ...S.tab, ...(mode === "USER" ? S.tabOn : null) }}
              onClick={() => setMode("USER")}
              type="button"
            >
              Single user
            </button>
            <button
              style={{ ...S.tab, ...(mode === "ALL" ? S.tabOn : null) }}
              onClick={() => setMode("ALL")}
              type="button"
            >
              Broadcast
            </button>
          </div>

          {mode === "USER" ? (
            <div style={{ marginTop: 12 }}>
              <div style={S.label}>Find user (name / email / phone)</div>
              <input
                style={S.input}
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder="Type at least 2 letters..."
              />

              <div style={{ marginTop: 10 }}>
                <div style={S.label}>Manual UID (best)</div>
                <input
                  style={S.input}
                  value={manualUid}
                  onChange={(e) => setManualUid(e.target.value)}
                  placeholder="Paste user UID here (NOT email/phone)"
                />
                <div style={S.help}>
                  Tip: open the user doc in <b>/users</b> — the document ID is the UID.
                </div>
              </div>

              {toUid ? (
                <div style={S.selected}>
                  Target UID: <b>{toUid}</b>{" "}
                  <button
                    style={S.smallBtn}
                    onClick={() => {
                      setSelectedUid("");
                      setManualUid("");
                    }}
                    type="button"
                  >
                    Clear
                  </button>
                </div>
              ) : null}

              {userResults.length > 0 && !selectedUid ? (
                <div style={S.list}>
                  {userResults.map((u) => (
                    <button
                      key={u.id}
                      style={S.listItem}
                      onClick={() => setSelectedUid(u.id)}
                      title={u.id}
                      type="button"
                    >
                      <div style={S.listTitle}>{u.displayName || "No name"}</div>
                      <div style={S.listSub}>{u.email || u.phone || u.id}</div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ marginTop: 12, fontWeight: 900, fontSize: 12, opacity: 0.8 }}>
              Broadcast will send to up to 500 users read from <b>/users</b>.
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <div style={S.label}>Type (optional)</div>
            <input style={S.input} value={type} onChange={(e) => setType(e.target.value)} placeholder="ADMIN_MESSAGE" />
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={S.label}>Title</div>
            <input
              style={S.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="E.g. Maintenance notice"
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={S.label}>Message</div>
            <textarea
              style={S.textarea}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write the notification..."
            />
          </div>

          {/* ✅ email toggle */}
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 900 }}>
              <input type="checkbox" checked={sendEmailToo} onChange={(e) => setSendEmailToo(e.target.checked)} />
              Send email too
            </label>
            <div style={{ fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.60)" }}>
              Emails go to <b>users/{`{uid}`}.email</b>
            </div>
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button
              style={{ ...S.primary, opacity: canSend ? 1 : 0.55 }}
              disabled={!canSend || sending}
              onClick={sendNow}
              type="button"
            >
              {sending ? "Sending..." : mode === "ALL" ? "Broadcast" : "Send"}
            </button>

            {msg ? <div style={S.msgOk}>{msg}</div> : null}
            {err ? <div style={S.msgErr}>{err}</div> : null}
          </div>

          {/* ✅ LIVE EMAIL DEBUG BOX (ALWAYS ON SCREEN) */}
          <div style={S.debugBox}>
            <div style={{ fontWeight: 950, fontSize: 13 }}>Email Debug</div>

            <div style={{ marginTop: 6, fontSize: 12, fontWeight: 900 }}>
              Status: <b>{emailDebug?.emailStatus || "—"}</b>
            </div>

            <div style={{ marginTop: 6, fontSize: 12, fontWeight: 800, opacity: 0.85 }}>
              {emailDebug?.emailDebug || "—"}
            </div>

            {emailDebug?.emailError ? (
              <div style={{ marginTop: 8, fontSize: 12, fontWeight: 900, color: "rgba(239,68,68,1)" }}>
                {emailDebug.emailError}
              </div>
            ) : null}

            {lastDocPath ? (
              <div style={{ marginTop: 8, fontSize: 11, fontWeight: 900, opacity: 0.7 }}>doc: {lastDocPath}</div>
            ) : null}

            <div style={{ marginTop: 8, fontSize: 11, fontWeight: 900, opacity: 0.65 }}>
              Tip: If Status stays on QUEUED/TRIGGERED, open Firebase Functions logs for more details.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- styles ---------------- */
const S = {
  page: {
    minHeight: "100vh",
    padding: 14,
    background: "linear-gradient(180deg,#f7f8ff 0%,#eef2ff 45%,#f8fafc 100%)",
  },
  top: {
    maxWidth: 980,
    margin: "0 auto 14px",
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },
  back: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
  },
  h1: { fontSize: 18, fontWeight: 950, color: "#0f172a" },
  sub: { marginTop: 2, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.60)" },
  badge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.9)",
    fontSize: 12,
    fontWeight: 900,
    color: "#0f172a",
  },

  wrap: { maxWidth: 980, margin: "0 auto" },
  panel: {
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
    padding: 14,
  },
  row: { display: "flex", gap: 10, flexWrap: "wrap" },
  tab: {
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.8)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
  },
  tabOn: { background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.22)" },

  label: { fontSize: 12, fontWeight: 900, color: "rgba(15,23,42,0.70)" },
  help: { marginTop: 6, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.55)" },

  input: {
    marginTop: 6,
    width: "100%",
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 800,
    background: "white",
  },
  textarea: {
    marginTop: 6,
    width: "100%",
    minHeight: 110,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 800,
    resize: "vertical",
    background: "white",
  },

  primary: {
    padding: "10px 14px",
    borderRadius: 14,
    border: "1px solid rgba(99,102,241,0.22)",
    background: "rgba(99,102,241,0.16)",
    cursor: "pointer",
    fontWeight: 950,
  },

  smallBtn: {
    padding: "8px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
  },

  msgOk: { fontSize: 12, fontWeight: 900, color: "rgba(34,197,94,1)" },
  msgErr: { fontSize: 12, fontWeight: 900, color: "rgba(239,68,68,1)" },

  list: { marginTop: 10, display: "grid", gap: 8 },
  listItem: {
    textAlign: "left",
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(248,250,252,0.75)",
    cursor: "pointer",
  },
  listTitle: { fontWeight: 950, fontSize: 13, color: "#0f172a" },
  listSub: { marginTop: 2, fontWeight: 800, fontSize: 12, color: "rgba(15,23,42,0.65)" },

  selected: {
    marginTop: 10,
    padding: 10,
    borderRadius: 14,
    border: "1px dashed rgba(15,23,42,0.25)",
    background: "rgba(255,255,255,0.7)",
    fontWeight: 900,
    fontSize: 12,
  },

  debugBox: {
    marginTop: 12,
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(2,6,23,0.03)",
  },
};